import React from "react";
import { Form, Spin, Tooltip } from "antd";
import DataGridConfigurationBuilder from "../../../core/ui/components/grid/DataGridConfigurationBuilder";

import { IGenericCodebookItem } from "../../../codebooks/model/GenericCodebookItem";
import { TAttachmentTypePresentationModel } from "../../../organisations/presenter/model/AttachmentTypePresentationModel";
import { TAttachmentPresentationModel } from "../../presenter/model/AttachmentPresentationModel";
import TickBox from "../../../core/ui/components/grid/TickBox";
import DataGrid from "../../../core/ui/components/grid/DataGrid";
import CaseLawDocumentAttachmentForm from "./CaseLawDocumentAttachmentForm";
import { ContentTypes } from "../../common/Constants";
import { ICaseLawDocument } from "../../model/CaseLawDocument";
import { AttachmentType } from "../../../attachmentType/common/Constants";

interface CaseLawDocumentAttachmentTableProps {
  translate: any;
  attachmentTypes: TAttachmentTypePresentationModel[];
  attachmentContentTypes: IGenericCodebookItem[];
  caseLawDocumentAttachments: TAttachmentPresentationModel[];
  createAttachment?: any;
  setFileValue?: any;
  updateAttachment?: any;
  uploadedFile: any;
  referenceLoader?: boolean;
  removeAttachment?: any;
  caseLawDocument?: ICaseLawDocument;
  editable: boolean;
  anonimisedUploadedFile?;
  setAnonymisedFileValue?;
  canDownload?: any;
}

const renderDownloadLink = (
  record: TAttachmentPresentationModel,
  editable,
  canDownload,
  caseLawDocument
) => {
  if (record.originalAttachmentId) {
    return record.contentType?.code === ContentTypes.URL_CONTENT_TYPE ? (
      <Tooltip title={record.content}>
        <a
          href={record.originalAttachmentContent}
          rel="noopener noreferrer"
          target={"_blank"}
        >
          <i className="fa fa-external-link-alt" aria-hidden="true" />
        </a>
      </Tooltip>
    ) : (!editable || record.identificationCode !== "Y") &&
      canDownload?.(caseLawDocument?.organisationId) ? (
      <a
        href={`/api/case-law-documents/${caseLawDocument?.id}/attachments/${record.originalAttachmentId}/download`}
      >
        <i className="fa fa-download" aria-hidden="true" />
      </a>
    ) : (
      <div></div>
    );
  } else
    return record.contentType?.code === ContentTypes.URL_CONTENT_TYPE ? (
      <Tooltip title={record.content}>
        <a href={record.content} rel="noopener noreferrer" target={"_blank"}>
          <i className="fa fa-external-link-alt" aria-hidden="true" />
        </a>
      </Tooltip>
    ) : !editable || record.identificationCode !== "Y" ? (
      <a
        href={`/api/case-law-documents/${caseLawDocument?.id}/attachments/${record.id}/download`}
      >
        <i className="fa fa-download" aria-hidden="true" />
      </a>
    ) : (
      <div></div>
    );
};
const CaseLawDocumentAttachmentTable: React.FC<CaseLawDocumentAttachmentTableProps> =
  ({
    translate,
    attachmentTypes,
    caseLawDocumentAttachments,
    createAttachment,
    setFileValue,
    uploadedFile,
    updateAttachment,
    referenceLoader,
    removeAttachment,
    caseLawDocument,
    editable,
    anonimisedUploadedFile,
    setAnonymisedFileValue,
    canDownload
  }) => {
    const [CreateForm] = Form.useForm();
    const [UpdateForm] = Form.useForm();
    const builder = DataGridConfigurationBuilder({
      translate,
      resourcePrefix: "editors.attachments"
    });
    const columns = builder.buildColumns(
      "name",
      {
        name: "attachment_type",
        dataKey: "attachmentType",
        render: (record: TAttachmentPresentationModel) => (
          <span>{record.attachmentTypeName}</span>
        )
      },
      "connectionType",
      "description",
      ...(canDownload?.(caseLawDocument?.organisationId)
        ? [
          {
            name: "anonymised",
            dataKey: "anonymised",
            sortable: false,
            align: "center",
            render: (record) =>
              record?.attachmentType?.anonymisationRequired && (
                <TickBox value={record.anonymised} />
              )
          }
        ]
        : []),
      ...(canDownload?.(caseLawDocument?.organisationId)
        ? [
          {
            name: "originalAttachment",
            dataKey: "originalAttachment",
            sortable: false,
            align: "center",
            render: (record: TAttachmentPresentationModel) => {
              return renderDownloadLink(
                record,
                editable,
                canDownload,
                caseLawDocument
              );
            }
          }
        ]
        : []),
      {
        name: "content",
        dataKey: "content",
        sortable: false,
        label: canDownload
          ? translate("editors.attachments.content")
          : translate("editors.attachments.originalAttachment"),
        align: "center",
        render: (record: TAttachmentPresentationModel) => {
          if (!record.anonymised) {
            if (!record.attachmentType.anonymisationRequired) {
              return renderDownloadLink(
                record,
                editable,
                canDownload,
                caseLawDocument
              );
            }
            return <></>;
          }
          return record.contentType?.code === ContentTypes.URL_CONTENT_TYPE ? (
            <Tooltip title={record.content}>
              <a
                href={record.content}
                rel="noopener noreferrer"
                target={"_blank"}
              >
                <i className="fa fa-external-link-alt" aria-hidden="true" />
              </a>
            </Tooltip>
          ) : (!editable || record.identificationCode !== "Y") ? (
            <a
              href={`/api/case-law-documents/attachments/${record.id}/download`}
            >
              <i className="fa fa-download" aria-hidden="true" />
            </a>
          ) : (
            <div></div>
          );
        }
      }
    );

    return (
      <Spin spinning={referenceLoader}>
        <DataGrid
          columns={columns}
          translate={translate}
          dataSource={caseLawDocumentAttachments || []}
          allowCreate={editable}
          allowUpdate={editable}
          allowDelete={editable}
          createActionPosition="topRight"
          addButtonText="Novi zapis"
          booleanColumnKeys={["anonymised"]}
          createModalClass={"modal-width"}
          updateModalClass={"modal-width"}
          keyProperty={"id"}
          CreateFormInstance={CreateForm}
          UpdateFormInstance={UpdateForm}
          createForm={
            <CaseLawDocumentAttachmentForm
              translate={translate}
              form={CreateForm}
              attachmentTypes={
                attachmentTypes?.filter(
                  (t) => t.name !== AttachmentType.DECISION
                ) || []
              }
              uploadedFile={uploadedFile}
              setFileValue={setFileValue}
              caseLawDocumentId={caseLawDocument?.id}
              anonimisedUploadedFile={anonimisedUploadedFile}
              setAnonymisedFileValue={setAnonymisedFileValue}
            />
          }
          createModalTitle={`${translate(
            "editors.attachments.create_modal_title"
          )}`}
          onCreate={(e) => {
            createAttachment(e);
          }}
          updateForm={
            <CaseLawDocumentAttachmentForm
              translate={translate}
              form={UpdateForm}
              attachmentTypes={attachmentTypes || []}
              uploadedFile={uploadedFile}
              setFileValue={setFileValue}
              caseLawDocumentId={caseLawDocument?.id}
              anonimisedUploadedFile={anonimisedUploadedFile}
              setAnonymisedFileValue={setAnonymisedFileValue}
            />
          }
          updateModalTitle={`${translate(
            "editors.attachments.update_modal_title"
          )}`}
          onUpdate={(e) => updateAttachment(e)}
          onStartUpdate={(rowData: TAttachmentPresentationModel) => {
            if (rowData.contentType.code === ContentTypes.FILE_CONTENT_TYPE) {
              setFileValue({ name: rowData.name, isDummy: true });
              rowData.originalAttachmentId &&
                setAnonymisedFileValue({ name: rowData.name, isDummy: true });
            }
          }}
          onDelete={(e) => {
            removeAttachment(e.id);
          }}
          deleteModalTitle={translate("editors.attachments.delete_modal_title")}
          deleteModalMessage={translate(
            "editors.attachments.delete_modal_message"
          )}
        />
      </Spin>
    );
  };
export default CaseLawDocumentAttachmentTable;
